/**
 * UploadQueueIndicator — Jobsite Capture Arc Stage 2A (2026-06-15).
 *
 * Floating chip that shows "N waiting to upload" whenever there are
 * pending or failed items in the offline upload queue. Tap to expand
 * into a panel with per-item state + retry / remove actions.
 *
 * The component renders nothing when the queue is empty so it never
 * gets in the way during normal app use.
 *
 * Stuck-while-hidden banner: when the page returns to visible AND items
 * have been pending for >60s (likely because Background Sync isn't
 * supported on this browser — iOS Safari), surface a "Uploads paused —
 * stay here to resume" hint so the user knows why.
 */
import React, { useState } from 'react';
import { CloudOff, CloudUpload, AlertCircle, RotateCw, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useUploadQueue } from '../../hooks/useUploadQueue';

export default function UploadQueueIndicator() {
  const {
    items, pendingCount, failedCount, uploadingCount,
    isOnline, isHidden, stuck,
    retry, remove, drainNow,
  } = useUploadQueue();

  const [expanded, setExpanded] = useState(false);

  // Don't render when there's nothing in the queue — keeps the screen
  // clean during normal use.
  if (items.length === 0) return null;

  const tone =
    failedCount > 0 ? 'bg-red-600 hover:bg-red-700' :
    !isOnline       ? 'bg-amber-600 hover:bg-amber-700' :
                      'bg-blue-600 hover:bg-blue-700';

  const label =
    !isOnline                       ? `${pendingCount} waiting — offline` :
    failedCount > 0                 ? `${failedCount} upload${failedCount === 1 ? '' : 's'} failed` :
    uploadingCount > 0              ? `Uploading ${uploadingCount} of ${pendingCount}…` :
    pendingCount > 0                ? `${pendingCount} waiting to upload` :
                                      'Uploads up to date';

  const Icon =
    !isOnline       ? CloudOff :
    failedCount > 0 ? AlertCircle :
                      CloudUpload;

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 pointer-events-none"
      data-testid="upload-queue-indicator-root"
    >
      {stuck.stuck && !isHidden && (
        <div
          className="pointer-events-auto px-3 py-2 rounded-md bg-amber-100 border border-amber-300 text-amber-900 text-xs shadow-lg max-w-xs"
          data-testid="upload-queue-stuck-banner"
        >
          Uploads paused while the app was backgrounded. Stay on this screen to let them finish.
        </div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`pointer-events-auto flex items-center gap-2 ${tone} text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg transition-colors`}
        data-testid="upload-queue-chip"
        aria-expanded={expanded}
      >
        <Icon size={16} className={uploadingCount > 0 ? 'animate-spin-slow' : ''} />
        <span data-testid="upload-queue-label">{label}</span>
        {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
      </button>

      {expanded && (
        <div
          className="pointer-events-auto w-80 max-h-[60vh] overflow-y-auto bg-white rounded-md shadow-xl border border-gray-200 text-gray-800 text-sm"
          data-testid="upload-queue-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="font-semibold text-xs text-gray-600 uppercase tracking-wide">Upload queue</span>
            <button
              type="button"
              onClick={() => { drainNow(); }}
              className="text-xs text-blue-600 hover:underline"
              data-testid="upload-queue-drain-now"
            >
              Retry all
            </button>
          </div>
          <ul className="divide-y divide-gray-100">
            {items.map((item) => (
              <li
                key={item.id}
                className="px-3 py-2 flex items-start gap-2"
                data-testid={`upload-queue-item-${item.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-800 truncate" title={item.meta?.label || item.endpoint}>
                    {item.meta?.label || item.parent_kind || item.endpoint}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {item.status === 'pending' && 'Pending'}
                    {item.status === 'uploading' && 'Uploading…'}
                    {item.status === 'failed' && (
                      <span className="text-red-600">
                        Failed{item.last_error ? ` — ${item.last_error}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {item.status === 'failed' && (
                    <button
                      type="button"
                      onClick={() => retry(item.id)}
                      className="text-gray-500 hover:text-blue-600"
                      title="Retry"
                      data-testid={`upload-queue-retry-${item.id}`}
                    >
                      <RotateCw size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(item.id)}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove"
                    data-testid={`upload-queue-remove-${item.id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
